import { forwardRef, useState } from "react";
import { bool, number, oneOf, string } from "prop-types";
import { useDragDropMonitor, useDraggable } from "@dnd-kit/react";
import { CELL_WIDTHS } from "@utils";
import { useEstimateEditorSettings } from "@tools";
import { Grid } from "@material-ui/core";
import { TimelineChartBar } from "../../TimelineChartBar/TimelineChartBar";
import classnames from "classnames";

export const TimelineSectionChartBar = forwardRef(({
  id,
  index,
  path,
  type,
  barWidth,
  editable = true,
  children,
  ...chartBarProps
}, ref) => {
  const { allowEdition } = useEstimateEditorSettings();
  const [ additionalWidth, setAdditionalWidth ] = useState(0);

  useDragDropMonitor({
    onDragMove(event) {
      if(event.operation.source.data?.path !== path)
        return;
      if(event.operation.source?.data?.action === "stretch") {
        const width = event.operation.position.current.x - event.operation.position.initial.x;
        if(barWidth + width < CELL_WIDTHS.VALUE.SMALL / 4) {
          event.preventDefault();
          event.operation?.reset?.();
        } else
          setAdditionalWidth(width);
      }
    },
    onDragEnd(event) {
      if(event.operation.source.data?.path !== path)
        return;
      if(event.operation.source?.data?.action === "stretch")
        setAdditionalWidth(0);
    }
  });

  const { ref: resizeRef, isDragging: isStretching } = useDraggable({
    id: `${id}-stretch-curtail`,
    index,
    type,
    data: { path, action: "stretch" },
    disabled: !allowEdition || !editable,
    attributes: {
      tabIndex: index,
      "aria-label": "Stretch or curtail timeline",
    },
  });

  return (
    <Grid
      item container
      wrap="nowrap"
      alignItems="center"
      className="hoverRow"
    >
      <TimelineChartBar
        ref={ref}
        draggable={allowEdition && editable}
        barWidth={barWidth + additionalWidth}
        animate={!isStretching}
        {...chartBarProps}
      >
        {children}
        {allowEdition && editable && barWidth > 1 && (
          <div
            ref={resizeRef}
            className={classnames(
              "cursor-resize apply-events absolute",
              !isStretching && "hoverIcon opaque"
            )}
            onMouseDown={e => e.stopPropagation()}
            style={{
              width: 4,
              right: -2,
              height: chartBarProps.height === "large" ? 40 : chartBarProps.height === "medium" ? 32 : 24,
              zIndex: 6,
            }}
          />
        )}
      </TimelineChartBar>
    </Grid>
  );
});

TimelineSectionChartBar.displayName = "TimelineSectionChartBar";

TimelineSectionChartBar.propTypes = {
  id: string.isRequired,
  index: number.isRequired,
  path: string.isRequired,
  type: oneOf(["section", "workType", "teamMember"]).isRequired,
  editable: bool,
  ...TimelineChartBar.propTypes,
}
